/**
 * Win/loss record and the current streak, from the customer's settled bets.
 * Pure, so it is tested; the meter (StreakMeter.tsx) only draws the result.
 *
 * Mirrors the prototype's `currentWinStreak()`: walk settled bets newest-first
 * and count consecutive wins; voids are skipped (a void is not a result).
 * `pending` bets are not settled and do not break a streak.
 */
import type { Bet } from '@/types/api';

export interface Record {
  won: number;
  lost: number;
  void: number;
  pending: number;
  settled: number;
  /** Consecutive wins at the head of the settled history. */
  currentStreak: number;
  /** Longest run of consecutive wins anywhere in the history. */
  bestStreak: number;
  /** won / (won + lost), 0 when nothing has been decided. */
  strikeRate: number;
  /** Net result on settled bets in cents: payouts received minus stakes risked (fees excluded — disclosed separately). */
  netCents: number;
}

export function computeRecord(bets: Bet[] | undefined): Record {
  const r: Record = { won: 0, lost: 0, void: 0, pending: 0, settled: 0, currentStreak: 0, bestStreak: 0, strikeRate: 0, netCents: 0 };
  if (!bets || bets.length === 0) return r;
  const sorted = [...bets].sort((a, b) => b.placedAt.localeCompare(a.placedAt));
  let headOpen = true;
  let run = 0;
  for (const b of sorted) {
    r[b.status]++;
    if (b.status === 'pending') continue;
    r.settled++;
    if (b.status === 'won') {
      r.netCents += (b.payoutCents ?? 0) - b.stakeCents;
      if (headOpen) r.currentStreak++;
    } else if (b.status === 'lost') {
      r.netCents -= b.stakeCents;
      headOpen = false;
    }
  }
  // bestStreak walks oldest → newest so a run is counted as it builds.
  for (const b of [...sorted].reverse()) {
    if (b.status === 'won') { run++; r.bestStreak = Math.max(r.bestStreak, run); }
    else if (b.status === 'lost') run = 0;
  }
  const decided = r.won + r.lost;
  r.strikeRate = decided === 0 ? 0 : r.won / decided;
  return r;
}

/** The prototype's meter thresholds: 🔥 at 3, 🔥🔥 at 5, 🔥🔥🔥 at 8+, bar full at 10. */
export function streakFlames(streak: number): string {
  return streak >= 8 ? '🔥🔥🔥' : streak >= 5 ? '🔥🔥' : streak >= 3 ? '🔥' : '';
}
export function streakBarPct(streak: number): number {
  return Math.min(100, (streak / 10) * 100);
}
export function streakColour(streak: number): string {
  return streak >= 5 ? '#ef4444' : streak >= 3 ? '#f97316' : '#eab308';
}
